/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { transformQuadFromPDFToView } from "dc-preview-utils/src/Utils/PageUtils";
import { isThereAnySelection } from "dc-preview-utils/src/selectors/touchSelectors";
import { pageContentHandler } from "pageContentHandler";
import { getContextMenuTriggerPageIndex } from "selectors/contextMenuSelectors";
import { getActiveVerb } from "selectors/quickToolsSelectors";
import { quickToolsVerbIdsForMarkupAnnots } from "constants/enums";

// quadsArray is received in pdf coordinates
function convertQuadsArrayToObject(quadsArray, stores, shouldTransformToViewQuads = true) {
  const quadsObj = quadsArray.reduce((nquads, quad) => {
    const { pageNum, quads: quadsInPage } = quad;
    const pageIndex = pageNum - 1;

    // set startPage & endPage
    const { startPage, endPage } = nquads;
    nquads.startPage = startPage >= 0 ? Math.min(startPage, pageIndex) : pageIndex;
    nquads.endPage = endPage >= 0 ? Math.max(endPage, pageIndex) : pageIndex;

    const zoomedQuadsInPage = quadsInPage.reduce((quadsInPageObj, quadInPage, idx) => {
      let quadPoints;
      if (shouldTransformToViewQuads) {
        quadPoints = transformQuadFromPDFToView(quadInPage, pageIndex, stores);
      } else {
        quadPoints = quadInPage;
      }
      const [x1, y1, x2, y2, x3, y3, x4, y4] = quadPoints;
      quadsInPageObj[`bbox${idx}`] = {
        quadPoints,
        deviceLeft: Math.min(x1, x3),
        deviceRight: Math.max(x1, x3),
        deviceTop: Math.min(y1, y3),
        deviceBottom: Math.max(y1, y3)
      };
      return quadsInPageObj;
    }, {});

    zoomedQuadsInPage.bboxCount = quadsInPage.length;
    nquads[`page${pageNum - 1}`] = zoomedQuadsInPage;

    return nquads;
  }, {});

  return quadsObj;
}

export function getZoomedQuadsObj(quadsArrayInPDFCoords, stores, shouldTransformToViewQuads = true) {
  const zoomedQuadsObj = convertQuadsArrayToObject(quadsArrayInPDFCoords, stores, shouldTransformToViewQuads);
  zoomedQuadsObj.newSelection = true;
  return zoomedQuadsObj;
}

export const getSelectedTextQuads = stores => {
  const params = {
    isThereAnySelection: isThereAnySelection(stores),
    pageIndex: getContextMenuTriggerPageIndex(stores)
  };
  if (params.isThereAnySelection) {
    const quadsObj = pageContentHandler.getTextManagerInstance().quads;
    params.quads = quadsObj;
    if (quadsObj && quadsObj.quadsArray) {
      params.quads = getZoomedQuadsObj(quadsObj.quadsArray, stores, false);
      params.viewQuads = getZoomedQuadsObj(quadsObj.quadsArray, stores);
    }
  }
  return params;
};

export function shouldDisableMultiPageSelection(stores) {
  return quickToolsVerbIdsForMarkupAnnots.includes(getActiveVerb(stores));
}
